import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useTenant } from "@/contexts/TenantContext";
import { Skeleton } from "@/components/ui/skeleton";
import { 
  TrendingUp, 
  TrendingDown, 
  DollarSign, 
  ShoppingCart, 
  Users, 
  Package,
  Calendar,
  ArrowUpRight
} from "lucide-react";
import { 
  BarChart, 
  Bar, 
  LineChart, 
  Line, 
  PieChart, 
  Pie, 
  Cell,
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer 
} from "recharts";
import { format, startOfMonth, endOfMonth, subMonths } from "date-fns";
import { ptBR } from "date-fns/locale";
import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

interface DashboardData {
  kpis: {
    receitaBruta: number;
    custoTotal: number;
    lucroBruto: number;
    totalVendas: number;
    ticketMedio: number;
    novosClientes: number;
  };
  receitaTemporal: Array<{ data: string; receita: number; custo: number; lucro: number }>;
  topProdutosReceita: Array<{ nome: string; receita: number }>;
  topProdutosQuantidade: Array<{ nome: string; quantidade: number }>;
  rentabilidadeProdutos: Array<{ 
    nome: string; 
    precoVenda: number; 
    precoCusto: number; 
    margem: number;
    receitaTotal: number;
  }>;
  topClientes: Array<{ nome: string; receita: number; frequencia: number }>;
  metodoPagamento: Array<{ metodo: string; valor: number }>;
  statusTransacoes: Array<{ status: string; quantidade: number; valor: number }>;
}

export default function Reports() {
  const { tenantId } = useTenant();
  const [drillDownData, setDrillDownData] = useState<any>(null);
  const [drillDownTitle, setDrillDownTitle] = useState("");
  const [selectedPeriod, setSelectedPeriod] = useState("month");

  const { data: dashboardData, isLoading } = useQuery({
    queryKey: ["dashboard-reports", tenantId, selectedPeriod],
    queryFn: async () => {
      if (!tenantId) return null;

      const now = new Date();
      let startDate = startOfMonth(now);
      let endDate = endOfMonth(now);

      if (selectedPeriod === "3months") {
        startDate = startOfMonth(subMonths(now, 2));
      } else if (selectedPeriod === "6months") {
        startDate = startOfMonth(subMonths(now, 5));
      } else if (selectedPeriod === "year") {
        startDate = startOfMonth(subMonths(now, 11));
      }

      // Buscar transações
      const { data: transactions } = await supabase
        .from("transactions")
        .select(`
          *,
          transaction_items (
            *,
            products (*)
          ),
          clients (nome, endereco_completo),
          profiles!vendedor_id (nome_completo)
        `)
        .eq("tenant_id", tenantId)
        .gte("created_at", startDate.toISOString())
        .lte("created_at", endDate.toISOString());

      // Buscar clientes novos
      const { data: newClients } = await supabase
        .from("clients")
        .select("*")
        .eq("tenant_id", tenantId)
        .gte("created_at", startDate.toISOString())
        .lte("created_at", endDate.toISOString());

      // Processar dados (entrada = vendas/receitas)
      const vendas = transactions?.filter(t => t.tipo === "entrada") || [];
      const receitaBruta = vendas.reduce((sum, t) => sum + Number(t.valor_total || 0), 0);
      
      // Calcular custo total baseado nos produtos vendidos
      let custoTotal = 0;
      vendas.forEach(venda => {
        venda.transaction_items?.forEach((item: any) => {
          const precoCusto = Number(item.products?.preco_custo || 0);
          custoTotal += precoCusto * item.quantidade;
        });
      });

      const lucroBruto = receitaBruta - custoTotal;
      const totalVendas = vendas.length;
      const ticketMedio = totalVendas > 0 ? receitaBruta / totalVendas : 0;
      const novosClientes = newClients?.length || 0;

      // Receita temporal (agregado por mês)
      const receitaPorMes = new Map<string, { receita: number; custo: number }>();
      vendas.forEach(venda => {
        const mes = format(new Date(venda.created_at), "MMM/yy", { locale: ptBR });
        const atual = receitaPorMes.get(mes) || { receita: 0, custo: 0 };
        
        let custoVenda = 0;
        venda.transaction_items?.forEach((item: any) => {
          custoVenda += Number(item.products?.preco_custo || 0) * item.quantidade;
        });

        receitaPorMes.set(mes, {
          receita: atual.receita + Number(venda.valor_total || 0),
          custo: atual.custo + custoVenda
        });
      });

      const receitaTemporal = Array.from(receitaPorMes.entries()).map(([data, valores]) => ({
        data,
        receita: valores.receita,
        custo: valores.custo,
        lucro: valores.receita - valores.custo
      }));

      // Top produtos por receita
      const produtosReceita = new Map<string, number>();
      vendas.forEach(venda => {
        venda.transaction_items?.forEach((item: any) => {
          const nome = item.products?.nome || "Produto desconhecido";
          produtosReceita.set(nome, (produtosReceita.get(nome) || 0) + Number(item.total || 0));
        });
      });

      const topProdutosReceita = Array.from(produtosReceita.entries())
        .map(([nome, receita]) => ({ nome, receita }))
        .sort((a, b) => b.receita - a.receita)
        .slice(0, 10);

      // Top produtos por quantidade
      const produtosQuantidade = new Map<string, number>();
      vendas.forEach(venda => {
        venda.transaction_items?.forEach((item: any) => {
          const nome = item.products?.nome || "Produto desconhecido";
          produtosQuantidade.set(nome, (produtosQuantidade.get(nome) || 0) + item.quantidade);
        });
      });

      const topProdutosQuantidade = Array.from(produtosQuantidade.entries())
        .map(([nome, quantidade]) => ({ nome, quantidade }))
        .sort((a, b) => b.quantidade - a.quantidade)
        .slice(0, 10);

      // Rentabilidade por produto
      const rentabilidade = new Map<string, { 
        precoVenda: number; 
        precoCusto: number; 
        receitaTotal: number;
        quantidadeVendida: number;
      }>();

      vendas.forEach(venda => {
        venda.transaction_items?.forEach((item: any) => {
          const nome = item.products?.nome || "Produto desconhecido";
          const atual = rentabilidade.get(nome) || { 
            precoVenda: 0, 
            precoCusto: 0, 
            receitaTotal: 0,
            quantidadeVendida: 0
          };
          
          rentabilidade.set(nome, {
            precoVenda: Number(item.products?.preco_venda || 0),
            precoCusto: Number(item.products?.preco_custo || 0),
            receitaTotal: atual.receitaTotal + Number(item.total || 0),
            quantidadeVendida: atual.quantidadeVendida + item.quantidade
          });
        });
      });

      const rentabilidadeProdutos = Array.from(rentabilidade.entries())
        .map(([nome, dados]) => ({
          nome,
          precoVenda: dados.precoVenda,
          precoCusto: dados.precoCusto,
          margem: dados.precoVenda > 0 
            ? ((dados.precoVenda - dados.precoCusto) / dados.precoVenda) * 100 
            : 0,
          receitaTotal: dados.receitaTotal
        }))
        .sort((a, b) => b.receitaTotal - a.receitaTotal);

      // Top clientes
      const clientesMap = new Map<string, { receita: number; frequencia: number }>();
      vendas.forEach(venda => {
        const nome = venda.clients?.nome || "Cliente não identificado";
        const atual = clientesMap.get(nome) || { receita: 0, frequencia: 0 };
        clientesMap.set(nome, {
          receita: atual.receita + Number(venda.valor_total || 0),
          frequencia: atual.frequencia + 1
        });
      });

      const topClientes = Array.from(clientesMap.entries())
        .map(([nome, dados]) => ({ nome, ...dados }))
        .sort((a, b) => b.receita - a.receita)
        .slice(0, 10);

      // Métodos de pagamento (a partir de transaction_payments confirmados de transações de entrada)
      const { data: pays } = await supabase
        .from('transaction_payments')
        .select(`
          valor, status, payment_method_id,
          transactions!inner(id, tenant_id, created_at, tipo)
        `)
        .eq('transactions.tenant_id', tenantId)
        .eq('transactions.tipo', 'entrada') // Apenas transações de entrada (vendas)
        .gte('transactions.created_at', startDate.toISOString())
        .lte('transactions.created_at', endDate.toISOString())
        .eq('status', 'confirmado');

      const metodoMap = new Map<string, number>();
      const methodRef = new Map<string, string>();
      // Carregar nomes de métodos
      const { data: pmRef } = await supabase.from('payment_methods').select('id,nome');
      (pmRef || []).forEach((m: any) => methodRef.set(m.id, m.nome));
      (pays || []).forEach((p: any) => {
        const id = p?.payment_method_id as string | null;
        const nome = id ? (methodRef.get(id) || id) : 'Não especificado';
        const valor = Number(p?.valor || 0);
        metodoMap.set(nome, (metodoMap.get(nome) || 0) + valor);
      });

      const metodoPagamento = Array.from(metodoMap.entries()).map(([metodo, valor]) => ({ metodo, valor }));

      // Status de transações (apenas entradas, para consistência com o resto do dashboard)
      const statusMap = new Map<string, { quantidade: number; valor: number }>();
      vendas.forEach(trans => {
        const status = trans.status || "pendente";
        const atual = statusMap.get(status) || { quantidade: 0, valor: 0 };
        statusMap.set(status, {
          quantidade: atual.quantidade + 1,
          valor: atual.valor + Number(trans.valor_total || 0)
        });
      });

      const statusTransacoes = Array.from(statusMap.entries())
        .map(([status, dados]) => ({ status, ...dados }));

      const result: DashboardData = {
        kpis: {
          receitaBruta,
          custoTotal,
          lucroBruto,
          totalVendas,
          ticketMedio,
          novosClientes
        },
        receitaTemporal,
        topProdutosReceita,
        topProdutosQuantidade,
        rentabilidadeProdutos,
        topClientes,
        metodoPagamento,
        statusTransacoes
      };

      return result;
    },
    enabled: !!tenantId
  });

  const handleDrillDown = async (type: string, value: any, chartType?: 'receita' | 'quantidade') => {
    if (!tenantId) return;

    // Calcular período (mesma lógica do query principal)
    const now = new Date();
    let startDate = startOfMonth(now);
    let endDate = endOfMonth(now);

    if (selectedPeriod === "3months") {
      startDate = startOfMonth(subMonths(now, 2));
    } else if (selectedPeriod === "6months") {
      startDate = startOfMonth(subMonths(now, 5));
    } else if (selectedPeriod === "year") {
      startDate = startOfMonth(subMonths(now, 11));
    }

    let query = supabase
      .from("transactions")
      .select(`
        *,
        transaction_items (
          *,
          products (nome)
        ),
        clients (nome),
        profiles!vendedor_id (nome_completo)
      `)
      .eq("tenant_id", tenantId)
      .gte("created_at", startDate.toISOString())
      .lte("created_at", endDate.toISOString())
      .eq("tipo", "entrada"); // Apenas vendas

    let title = "Detalhes";

    switch (type) {
      case "produto":
        const { data: prodData } = await query;
        let totalCalculado = 0;
        const filteredByProduct = prodData
          ?.filter(t => 
            t.transaction_items?.some((item: any) => item.products?.nome === value)
          )
          .map((t: any) => {
            // Calcular valor específico deste produto nesta transação
            const itemsDoProduto = (t.transaction_items || []).filter(
              (item: any) => item.products?.nome === value
            );
            
            let valorProduto = 0;
            let quantidadeProduto = 0;
            
            itemsDoProduto.forEach((item: any) => {
              if (chartType === 'receita') {
                valorProduto += Number(item.total || 0);
              } else if (chartType === 'quantidade') {
                quantidadeProduto += Number(item.quantidade || 0);
              }
            });
            
            totalCalculado += chartType === 'receita' ? valorProduto : quantidadeProduto;
            
            return {
              ...t,
              valor_produto: valorProduto,
              quantidade_produto: quantidadeProduto,
            };
          }) || [];
        
        title = chartType === 'receita' 
          ? `Transações - ${value} - Receita (Total: ${new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(totalCalculado)})`
          : `Transações - ${value} - Quantidade (Total: ${totalCalculado} unidades)`;
        setDrillDownData(filteredByProduct);
        break;
      
      case "cliente":
        const { data: clientData } = await query;
        const filteredByClient = clientData?.filter(t => 
          t.clients?.nome === value
        ) || [];
        
        // Calcular receita total e frequência (mesma lógica do gráfico)
        const receitaCliente = filteredByClient.reduce((sum, t) => sum + Number(t.valor_total || 0), 0);
        const frequenciaCliente = filteredByClient.length;
        
        title = chartType === 'receita'
          ? `Transações - ${value} - Receita (Total: ${new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(receitaCliente)})`
          : chartType === 'quantidade'
          ? `Transações - ${value} - Frequência (Total: ${frequenciaCliente} transação(ões))`
          : `Transações - ${value} (Receita: ${new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(receitaCliente)}, Frequência: ${frequenciaCliente})`;
        
        setDrillDownData(filteredByClient);
        break;
      
      case "metodo": {
        // Buscar ID do método de pagamento
        const { data: methodData } = await supabase
          .from('payment_methods')
          .select('id')
          .eq('nome', value)
          .maybeSingle();
        
        const methodId = methodData?.id || '00000000-0000-0000-0000-000000000000';
        
        // Buscar transaction_payments confirmados com esse método no período (mesma lógica do gráfico)
        const { data: paysWithMethod } = await supabase
          .from('transaction_payments')
          .select(`
            valor, transaction_id,
            transactions!inner(*, clients(nome), profiles!vendedor_id(nome_completo))
          `)
          .eq('payment_method_id', methodId)
          .eq('status', 'confirmado')
          .eq('transactions.tenant_id', tenantId)
          .eq('transactions.tipo', 'entrada') // Apenas transações de entrada (vendas)
          .gte('transactions.created_at', startDate.toISOString())
          .lte('transactions.created_at', endDate.toISOString());
        
        // Agrupar por transação e calcular valor do método por transação
        const transactionValueMap = new Map<string, number>();
        (paysWithMethod || []).forEach((p: any) => {
          const txId = p.transaction_id;
          if (txId) {
            const currentValue = transactionValueMap.get(txId) || 0;
            transactionValueMap.set(txId, currentValue + Number(p?.valor || 0));
          }
        });
        
        // Buscar transações únicas e adicionar valor calculado
        const transactionIds = Array.from(transactionValueMap.keys());
        const txsMap = new Map();
        (paysWithMethod || []).forEach((p: any) => {
          if (p.transactions && !txsMap.has(p.transactions.id)) {
            txsMap.set(p.transactions.id, {
              ...p.transactions,
              valor_metodo: transactionValueMap.get(p.transactions.id) || 0,
            });
          }
        });
        
        const txs = Array.from(txsMap.values());
        const totalMetodo = Array.from(transactionValueMap.values()).reduce((sum, val) => sum + val, 0);
        
        title = `Transações - Método: ${value} (Total: ${new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(totalMetodo)})`;
        setDrillDownData(txs);
        break; }
      
      case "status":
        // Normalizar o status para minúsculas (os valores no banco são: "pendente", "paga", "cancelada")
        const normalizedStatus = value?.toLowerCase() || "pendente";
        const { data: statusData } = await query.eq("status", normalizedStatus);
        const filteredByStatus = statusData || [];
        
        // Calcular quantidade e valor total (mesma lógica do gráfico)
        const quantidadeStatus = filteredByStatus.length;
        const valorStatus = filteredByStatus.reduce((sum, t) => sum + Number(t.valor_total || 0), 0);
        
        title = `Transações - Status: ${value} (Quantidade: ${quantidadeStatus}, Valor Total: ${new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(valorStatus)})`;
        setDrillDownData(filteredByStatus);
        break;
    }

    setDrillDownTitle(title);
  };

  const COLORS = ['hsl(var(--primary))', 'hsl(var(--secondary))', 'hsl(var(--accent))', 'hsl(var(--muted))'];

  if (isLoading) {
    return (
      <div className="space-y-6 p-6">
        <Skeleton className="h-12 w-64" />
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {[...Array(6)].map((_, i) => (
            <Skeleton key={i} className="h-32" />
          ))}
        </div>
      </div>
    );
  }

  if (!dashboardData) {
    return (
      <div className="p-6">
        <p className="text-muted-foreground">Nenhum dado disponível</p>
      </div>
    );
  }

  return (
    <div className="space-y-8 p-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-4xl font-bold tracking-tight">Relatórios e Análises</h1>
          <p className="text-muted-foreground mt-2">Dashboard complementar de performance do negócio</p>
        </div>
        <Tabs value={selectedPeriod} onValueChange={setSelectedPeriod}>
          <TabsList>
            <TabsTrigger value="month">Mês</TabsTrigger>
            <TabsTrigger value="3months">3 Meses</TabsTrigger>
            <TabsTrigger value="6months">6 Meses</TabsTrigger>
            <TabsTrigger value="year">Ano</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {/* Seção 1: KPIs Globais */}
      <section>
        <h2 className="text-2xl font-semibold mb-4 flex items-center gap-2">
          <TrendingUp className="h-6 w-6" />
          Visão Macroeconômica
        </h2>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          <Card className="hover:shadow-lg transition-shadow cursor-pointer">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Receita Bruta</CardTitle>
              <DollarSign className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(dashboardData.kpis.receitaBruta)}
              </div>
            </CardContent>
          </Card>

          <Card className="hover:shadow-lg transition-shadow cursor-pointer">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Custo Total</CardTitle>
              <TrendingDown className="h-4 w-4 text-destructive" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-destructive">
                {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(dashboardData.kpis.custoTotal)}
              </div>
            </CardContent>
          </Card>

          <Card className="hover:shadow-lg transition-shadow cursor-pointer">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Lucro Bruto</CardTitle>
              <ArrowUpRight className="h-4 w-4 text-green-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-500">
                {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(dashboardData.kpis.lucroBruto)}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Margem: {dashboardData.kpis.receitaBruta > 0 
                  ? ((dashboardData.kpis.lucroBruto / dashboardData.kpis.receitaBruta) * 100).toFixed(1) 
                  : 0}%
              </p>
            </CardContent>
          </Card>

          <Card className="hover:shadow-lg transition-shadow cursor-pointer">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total de Vendas</CardTitle>
              <ShoppingCart className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{dashboardData.kpis.totalVendas}</div>
            </CardContent>
          </Card>

          <Card className="hover:shadow-lg transition-shadow cursor-pointer">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Ticket Médio</CardTitle>
              <Calendar className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(dashboardData.kpis.ticketMedio)}
              </div>
            </CardContent>
          </Card>

          <Card className="hover:shadow-lg transition-shadow cursor-pointer">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Novos Clientes</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{dashboardData.kpis.novosClientes}</div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Seção 2: Evolução Temporal */}
      <section>
        <h2 className="text-2xl font-semibold mb-4">Análise Temporal - Receita, Custo e Lucro</h2>
        <Card>
          <CardContent className="pt-6">
            <ResponsiveContainer width="100%" height={400}>
              <LineChart data={dashboardData.receitaTemporal}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="data" />
                <YAxis />
                <Tooltip 
                  formatter={(value: number) => new Intl.NumberFormat('pt-BR', { 
                    style: 'currency', 
                    currency: 'BRL' 
                  }).format(value)}
                />
                <Legend />
                <Line type="monotone" dataKey="receita" stroke="hsl(var(--primary))" name="Receita" strokeWidth={2} />
                <Line type="monotone" dataKey="custo" stroke="hsl(var(--destructive))" name="Custo" strokeWidth={2} />
                <Line type="monotone" dataKey="lucro" stroke="hsl(142, 76%, 36%)" name="Lucro" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </section>

      {/* Seção 3: Análise de Produtos */}
      <section>
        <h2 className="text-2xl font-semibold mb-4 flex items-center gap-2">
          <Package className="h-6 w-6" />
          Análise de Produtos e Rentabilidade
        </h2>
        
        <div className="grid gap-4 md:grid-cols-2 mb-4">
          <Card>
            <CardHeader>
              <CardTitle>Top 10 Produtos - Receita</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={dashboardData.topProdutosReceita}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="nome" angle={-45} textAnchor="end" height={100} />
                  <YAxis />
                  <Tooltip 
                    formatter={(value: number) => new Intl.NumberFormat('pt-BR', { 
                      style: 'currency', 
                      currency: 'BRL' 
                    }).format(value)}
                  />
                  <Bar 
                    dataKey="receita" 
                    fill="hsl(var(--primary))" 
                    onClick={(data) => handleDrillDown("produto", data.nome, "receita")}
                    cursor="pointer"
                  />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Top 10 Produtos - Quantidade</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={dashboardData.topProdutosQuantidade}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="nome" angle={-45} textAnchor="end" height={100} />
                  <YAxis />
                  <Tooltip />
                  <Bar 
                    dataKey="quantidade" 
                    fill="hsl(var(--secondary))"
                    onClick={(data) => handleDrillDown("produto", data.nome, "quantidade")}
                    cursor="pointer"
                  />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Rentabilidade por Produto</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Produto</TableHead>
                  <TableHead>Preço Venda</TableHead>
                  <TableHead>Preço Custo</TableHead>
                  <TableHead>Margem (%)</TableHead>
                  <TableHead>Receita Total</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {dashboardData.rentabilidadeProdutos.map((prod, idx) => (
                  <TableRow 
                    key={idx} 
                    className="cursor-pointer hover:bg-muted"
                    onClick={() => handleDrillDown("produto", prod.nome)}
                  >
                    <TableCell className="font-medium">{prod.nome}</TableCell>
                    <TableCell>
                      {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(prod.precoVenda)}
                    </TableCell>
                    <TableCell>
                      {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(prod.precoCusto)}
                    </TableCell>
                    <TableCell className={prod.margem > 30 ? "text-green-500 font-bold" : prod.margem < 10 ? "text-destructive font-bold" : ""}>
                      {prod.margem.toFixed(1)}%
                    </TableCell>
                    <TableCell>
                      {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(prod.receitaTotal)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </section>

      {/* Seção 4: Análise de Clientes */}
      <section>
        <h2 className="text-2xl font-semibold mb-4 flex items-center gap-2">
          <Users className="h-6 w-6" />
          Análise de Clientes e Comportamento
        </h2>
        
        <div className="grid gap-4 md:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle>Top 10 Clientes - Receita</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={dashboardData.topClientes}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="nome" angle={-45} textAnchor="end" height={100} />
                  <YAxis />
                  <Tooltip 
                    formatter={(value: number) => new Intl.NumberFormat('pt-BR', { 
                      style: 'currency', 
                      currency: 'BRL' 
                    }).format(value)}
                  />
                  <Bar 
                    dataKey="receita" 
                    fill="hsl(var(--primary))"
                    onClick={(data) => handleDrillDown("cliente", data.nome, "receita")}
                    cursor="pointer"
                  />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Top 10 Clientes - Frequência</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={dashboardData.topClientes}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="nome" angle={-45} textAnchor="end" height={100} />
                  <YAxis />
                  <Tooltip />
                  <Bar 
                    dataKey="frequencia" 
                    fill="hsl(var(--accent))"
                    onClick={(data) => handleDrillDown("cliente", data.nome, "quantidade")}
                    cursor="pointer"
                  />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Seção 5: Análise Financeira */}
      <section>
        <h2 className="text-2xl font-semibold mb-4 flex items-center gap-2">
          <DollarSign className="h-6 w-6" />
          Análise Financeira e Métodos de Pagamento
        </h2>
        
        <div className="grid gap-4 md:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle>Distribuição por Método de Pagamento</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={dashboardData.metodoPagamento}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ metodo, percent }) => `${metodo}: ${(percent * 100).toFixed(0)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="valor"
                    onClick={(data) => handleDrillDown("metodo", data.metodo)}
                    cursor="pointer"
                  >
                    {dashboardData.metodoPagamento.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip 
                    formatter={(value: number) => new Intl.NumberFormat('pt-BR', { 
                      style: 'currency', 
                      currency: 'BRL' 
                    }).format(value)}
                  />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Status das Transações</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Status</TableHead>
                    <TableHead>Quantidade</TableHead>
                    <TableHead>Valor Total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {dashboardData.statusTransacoes.map((item, idx) => (
                    <TableRow 
                      key={idx}
                      className="cursor-pointer hover:bg-muted"
                      onClick={() => handleDrillDown("status", item.status)}
                    >
                      <TableCell className="font-medium capitalize">{item.status}</TableCell>
                      <TableCell>{item.quantidade}</TableCell>
                      <TableCell>
                        {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(item.valor)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Dialog para Drill-Down */}
      <Dialog open={!!drillDownData} onOpenChange={() => setDrillDownData(null)}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{drillDownTitle}</DialogTitle>
          </DialogHeader>
          {drillDownData && (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Data</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Cliente</TableHead>
                  <TableHead>Vendedor</TableHead>
                  {drillDownTitle.includes('Produto') && drillDownTitle.includes('Receita') && (
                    <TableHead className="text-right">Valor do Produto</TableHead>
                  )}
                  {drillDownTitle.includes('Produto') && drillDownTitle.includes('Quantidade') && (
                    <TableHead className="text-right">Quantidade do Produto</TableHead>
                  )}
                  {drillDownTitle.includes('Método:') && (
                    <TableHead className="text-right">Valor do Método</TableHead>
                  )}
                  {!drillDownTitle.includes('Produto') && !drillDownTitle.includes('Método:') && (
                    <TableHead>Valor</TableHead>
                  )}
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {drillDownData.map((trans: any) => (
                  <TableRow key={trans.id}>
                    <TableCell>
                      {format(new Date(trans.created_at), "dd/MM/yyyy HH:mm", { locale: ptBR })}
                    </TableCell>
                    <TableCell className="capitalize">{trans.tipo}</TableCell>
                    <TableCell>{trans.clients?.nome || "N/A"}</TableCell>
                    <TableCell>{trans.profiles?.nome_completo || "N/A"}</TableCell>
                    {drillDownTitle.includes('Produto') && drillDownTitle.includes('Receita') && (
                      <TableCell className="text-right font-semibold">
                        {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(trans.valor_produto || 0)}
                      </TableCell>
                    )}
                    {drillDownTitle.includes('Produto') && drillDownTitle.includes('Quantidade') && (
                      <TableCell className="text-right font-semibold">
                        {trans.quantidade_produto || 0} unidade(s)
                      </TableCell>
                    )}
                    {drillDownTitle.includes('Método:') && (
                      <TableCell className="text-right font-semibold">
                        {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(trans.valor_metodo || 0)}
                      </TableCell>
                    )}
                    {!drillDownTitle.includes('Produto') && !drillDownTitle.includes('Método:') && (
                      <TableCell>
                        {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(trans.valor_total)}
                      </TableCell>
                    )}
                    <TableCell className="capitalize">{trans.status}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
